// Getting around. Short trips are walks and small hops along a route of nearby surfaces.
// Long trips use the general ways of the companion's world (see catalog.js): Minecraft mobs
// take the elytra with rockets, a slime block, an ender pearl or a nether portal; everyone else
// a jetpack, a grappling hook or a wormhole. The parachute is for everyone. (The first jetpack
// is kept for comparison.)

import { clockNow } from './clock.js';
import { env, clamp, rand, chance, pick, lerp, easeInOut, easeOut, tick, wait, animate, weighted } from './core.js';
import { setShape, pulse, applyRest, spotPoint, spotScale, fits, seatX, resetPose, handPoint, cast } from './companion.js';
import { planRoute, surfaceBelow, FLOOR, floorY, windowList, visibleOn } from './world.js';
import { props, portalBlocks, gunMuzzle } from './stage.js';
import { WORMHOLE_COLORS } from './props.js';
import * as fx from './particles.js';
import { hooks } from './hooks.js';
import { can, walkStyle, ownTravel } from './catalog.js';
import { ability } from './abilities.js';

// The surface a spot stands on (a lean spot stands on a ledge; inside the search bar it's reached over the bar's top).
export const baseOf = spot => spot.base || spot.ledge.base || spot.ledge;
const ENDER = ['#B05CE0', '#7A2BD9', '#E0B3FF', '#5A1AA8'];

// ---------- the main entry ----------
// A seat (a surface with `at`: the end of a line of text) holds one companion, right at its end,
// and nobody goes where it doesn't fit (`room`). A trip there that an ability chose itself is pinned
// to the seat, or doesn't happen when the seat is someone else's or too low: it stays where it is.
function allowed(me, spot) {
  const l = spot.ledge, g = spot.rest !== 'lean' && l !== FLOOR && l.geom?.();
  if (!g) return spot;
  if (!fits(me, l)) return null;
  const x = seatX(me, g, spotScale(me, { ledge: l }));
  if (x == null) return spot;
  const mine = o => (o.anchor && baseOf(o.anchor) === l) || (o.dest && baseOf(o.dest) === l);
  if (cast.some(o => o !== me && o.alpha > 0 && mine(o))) return null;
  return { ...spot, dx: x - g.x1 };
}

export async function travelTo(spot, g, how) {
  const me = g.me;
  if (spot) spot = allowed(me, spot);
  if (!spot || !spotPoint(me, spot)) return;
  const from = me.anchor;
  me.dest = spot; me.moving = true; me.anchor = null;
  const wasSitting = me.pose.sit;
  resetPose(me); setShape(me, 'normal', 'front');
  if (wasSitting) await wait(160, g);               // stand up first
  const target = baseOf(spot), start = from ? baseOf(from) : null;
  // Inside the search bar it first hops back up onto the bar.
  const sg = start?.geom();
  if (sg && Math.abs(sg.y - me.y) > 2) await hopTo(() => { const q = start.geom(); return q && { x: clamp(me.x, q.x1, q.x2), y: q.y }; }, g, Math.min(env.S, sg.s ?? env.S));
  // Where it was going can go away meanwhile (a page scrolled it off): it stays where it is.
  if (!spotPoint(me, spot)) { await fallDown(g); return; }
  let way = how;
  if (!way) {
    if (start && start.id === target.id) way = 'walk';
    else {
      const route = start && start.kind !== 'chair' && start.kind !== 'caret' ? planRoute(start, target, env.S) : null;
      way = route && route.length <= 4 ? { route } : longWay(me, spot);
    }
  }
  // A companion that can't walk goes its own way for everything; a flier flies instead of
  // hopping from surface to surface.
  const style = walkStyle(me.design), own = ownTravel(me.design).find(w => can(me, w) && ability(w)?.travel);
  if (own && ((style === 'none' && (way === 'walk' || way === 'hop')) || ((style === 'none' || style === 'fly') && way.route))) way = own;
  let ok = true;
  if (way === 'walk') ok = await walkOn(target, spotPoint(me, spot)?.x ?? me.x, g);
  else if (way.route) ok = await followRoute(way.route, spot, g);
  else if (WAYS[way]) await WAYS[way](spot, g, start);
  else if (ability(way)?.travel) await ability(way).travel(spot, g, start);
  else await WAYS.hop(spot, g, start);
  if (!ok) { await fallDown(g); return; }
  // A spot below its surface (inside the search bar) takes a last small hop down.
  const pt = spotPoint(me, spot);
  if (pt && Math.abs(pt.y - me.y) > 2) await hopTo(() => spotPoint(me, spot), g, spotScale(me, spot));
  arrive(me, spot);
}

export function arrive(me, spot) {
  me.moving = false; me.dest = null; me.anchor = spot;
  me.s = spotScale(me, spot);
  const pt = spotPoint(me, spot);
  if (pt) { me.x = pt.x; me.y = pt.y; }
  me.jump = 0; me.bob = 0;
  applyRest(me, spot);
}

// The general ways and how likely each is, going up, down or across. A companion only uses
// those of its own world (see catalog.js), so a Minecraft mob picks among the elytra, ender
// pearl and nether portal as often as the others pick the jetpack, grappling hook and wormhole.
// The order is also the order it prefers them in (generalWay).
const GENERAL_WAYS = {
  up: [['elytra', 6], ['jetpack', 6], ['pearl', 3], ['hook', 3], ['portal', 2], ['wormhole', 2]],
  down: [['parachute', 4], ['slime', 3], ['pearl', 2], ['hook', 2], ['portal', 1.5], ['wormhole', 1.5], ['jetpack', 1]],
  across: [['elytra', 4], ['jetpack', 4], ['pearl', 3], ['hook', 3], ['portal', 2], ['wormhole', 2]],
};
// The general way up (or down) it would most likely take: the first it uses (or that `ok` allows).
export const generalWay = (me, dir, ok = way => can(me, way)) => GENERAL_WAYS[dir].find(([way]) => ok(way))?.[0];

// How to get somewhere far: one of the ways this companion may use (see catalog.js): its own
// ways (flying, digging, teleporting) and the general ones, weighted by where the spot is. With
// none of them allowed it just hops there.
function longWay(me, spot) {
  const goal = spotPoint(me, spot), s = me.s, dy = goal.y - me.y, dist = Math.hypot(goal.x - me.x, dy);
  if (dist < 26 * s && dy > -12 * s && walkStyle(me.design) !== 'none') return 'hop';
  const lean = spot.rest === 'lean';                         // no slime block to bounce on by a wall
  const opts = GENERAL_WAYS[dy < -12 * s ? 'up' : dy > 26 * s ? 'down' : 'across'].map(([way, w]) => [way, way === 'slime' && lean ? 0 : w]);
  // Its own ways, except one that only goes down (a chicken fluttering) when it isn't going down.
  for (const way of ownTravel(me.design)) if (ability(way)?.travel && (!ability(way).down || dy > 26 * s)) opts.push([way, 6]);
  return weighted(opts.filter(([way]) => can(me, way)), o => o[1])?.[0] || 'hop';
}

// A way that only goes down (a chicken fluttering), asked to go up, takes the general way up
// it uses most, or jumps.
export async function upInstead(spot, g, from) {
  const up = generalWay(g.me, 'up');
  if (up) await WAYS[up](spot, g, from); else await hopTo(() => spotPoint(g.me, spot), g, spotScale(g.me, spot));
}

// ---------- walking and hopping ----------
// How it walks along a surface depends on its walk style (see catalog.js): Clawd and the app
// companions shuffle sideways facing you, Minecraft mobs turn and walk in profile with their
// legs swinging, rollers roll, fliers glide, hoppers hop, and one that can't walk goes its own way.
// style: a walk style to use instead of its design's (its own way of walking can walk an engine
// style with something of its own added: Persephone's flowers).
export async function walkOn(ledge, toX, g, { stayTurned = false, style = walkStyle(g.me.design) } = {}) {   // stayTurned: it doesn't turn back to face you
  const me = g.me;
  let geo = ledge.geom();
  if (!geo) return false;
  let dx = me.x - geo.x1;
  const goal = toX - geo.x1, dir = Math.sign(goal - dx);
  if (!dir) return true;
  if (style === 'hop') return hopAlong(ledge, goal, g);
  if (style === 'none') return blinkTo(ledge, goal, g);
  const own = ability(style)?.walk;                           // its own way of walking (a penguin's waddle)
  if (own) return own(ledge, toX, g, { stayTurned });
  const p = me.pose, rolls = style === 'roll', profile = style === 'profile', flies = style === 'fly';
  p.eyes = dir < 0 ? 'left' : 'right';
  const speed = (rolls ? 18 : flies ? 14 : profile ? 11 : 12) * me.s * (me.hurry ? 2 : 1)   // hurry: running away
    * (me.design.moves?.pace ?? 1);                            // a heavy mob takes its time (the Iron Golem)
  if (rolls) { p.legs = 'none'; p.armL = p.armR = 'none'; me.rotAround = 'center'; }
  if (profile) {                                              // turn to face the way it goes, then walk
    setShape(me, 'side', 'side', dir);
    await wait(200, g);
    p.legs = 'walk'; p.stride = 0;
  }
  const radius = me.shape.bh * me.s / 2;
  let last = clockNow(), legT = 0, legB = false;
  while (Math.abs(goal - dx) > 0.5) {
    await tick(g);
    const now = clockNow(), dt = Math.min(0.1, (now - last) / 1000); last = now;
    const stepX = dir * Math.min(Math.abs(goal - dx), speed * dt);
    dx += stepX;
    geo = ledge.geom();
    if (!geo) return false;
    me.x = geo.x1 + dx; me.y = geo.y;
    if (rolls) { me.rot += stepX / radius; continue; }          // a ball rolls: it turns by the distance covered
    if (profile) { p.stride += Math.abs(stepX) / (2.2 * me.s); continue; }   // the legs swing with the distance
    if (flies) { me.bob = Math.round(Math.sin(now / 150) * 0.7 * me.s); continue; }
    legT += dt;
    if (legT > 0.11) { legT = 0; legB = !legB; p.legs = legB ? 'walkB' : 'walkA'; me.bob = legB ? -Math.max(1, Math.round(me.s * 0.4)) : 0; }
  }
  if (rolls) {                                                 // settle upright, then legs and arms come back out
    const r0 = me.rot % (2 * Math.PI), r1 = Math.round(r0 / (2 * Math.PI)) * 2 * Math.PI;
    await animate(220, g, t => { me.rot = r0 + (r1 - r0) * t; });
    me.rot = 0; me.rotAround = 'feet'; p.armL = p.armR = 'side';
  }
  p.legs = 'stand'; p.stride = 0; me.bob = 0; p.eyes = 'center';
  if (profile && !stayTurned) { setShape(me, 'normal', 'front'); await wait(160, g); }
  return true;
}

// Hoppers (slimes, frogs) get along a surface in small hops.
async function hopAlong(ledge, goal, g) {
  const me = g.me, s = me.s;
  for (let guard = 0; guard < 60; guard++) {
    const geo = ledge.geom();
    if (!geo) return false;
    const left = goal - (me.x - geo.x1);
    if (Math.abs(left) < 0.5) return true;
    const to = (me.x - geo.x1) + Math.sign(left) * Math.min(Math.abs(left), 9 * s);   // along the surface
    const ok = await hopTo(() => { const q = ledge.geom(); return q && { x: q.x1 + to, y: q.y }; }, g);
    if (!ok) return false;
  }
  return true;
}

// One that can't walk (the Shulker) goes its own way even for a short move along its surface.
async function blinkTo(ledge, goal, g) {
  const me = g.me, own = ownTravel(me.design).find(w => ability(w)?.travel);
  if (own) await ability(own).travel({ ledge, dx: goal, rest: 'stand' }, g);
  else { const geo = ledge.geom(); if (!geo) return false; me.x = geo.x1 + goal; }
  return true;
}

// A small jump to target() (read live). The size steps gradually if the destination needs
// a different size (text, inside the search bar).
export async function hopTo(target, g, toScale = g.me.s) {
  const me = g.me;
  const own = ability(me.design.moves?.hop)?.hop;   // its own way of hopping ("hop" in its moves: a slime's squishy bounce)
  if (own) return own(target, g, toScale);
  const p = me.pose;
  pulse(me, 'squash'); await wait(100, g);
  const x0 = me.x, y0 = me.y, s0 = me.s;
  let end = target();
  if (!end) return false;
  const dist = Math.hypot(end.x - x0, end.y - y0);
  const height = clamp(dist * 0.3, 3 * s0, 14 * s0) + Math.max(0, y0 - end.y) * 0.3;
  const dur = clamp(dist * 1.4, 330, 720);
  p.eyes = end.x < x0 - 2 ? 'left' : end.x > x0 + 2 ? 'right' : end.y < y0 ? 'up' : 'down';
  Object.assign(p, { armL: 'up', armR: 'up', legs: 'walkB' });
  setShape(me, 'stretch');
  await animate(dur, g, t => {
    end = target() || end;
    me.x = lerp(x0, end.x, t); me.y = lerp(y0, end.y, t);
    me.jump = 4 * height * t * (1 - t);
    if (t > 0.45) setShape(me, 'normal');
    me.s = Math.round(lerp(s0, toScale, clamp((t - 0.35) / 0.3, 0, 1)));
  });
  me.jump = 0;
  Object.assign(p, { armL: 'side', armR: 'side', legs: 'stand' });
  pulse(me, 'squash'); await wait(90, g);
  p.eyes = 'center';
  return true;
}

async function followRoute(route, spot, g) {
  const me = g.me;
  // Remember each step relative to its ledge, so moving windows don't throw it off.
  const steps = route.map(h => ({ from: h.from, to: h.to, tdx: h.takeoff - h.from.geom().x1, ldx: h.land - h.to.geom().x1 }));
  for (const st of steps) {
    const ga = st.from.geom();
    if (!ga || !(await walkOn(st.from, ga.x1 + st.tdx, g))) return false;
    await wait(rand(60, 180), g);
    const to = st.to.geom();
    if (!to) return false;
    const ok = await hopTo(() => { const gb = st.to.geom(); return gb ? { x: gb.x1 + st.ldx, y: gb.y } : null; }, g, Math.min(env.S, to.s ?? env.S));
    if (!ok) return false;
  }
  const pt = spotPoint(me, spot);
  return pt ? walkOn(baseOf(spot), pt.x, g) : false;
}

// ---------- falling (dropped, or the surface went away) ----------
export async function fallDown(g) {
  const me = g.me;
  const s = me.s, below = surfaceBelow(me.x, me.y + 1, undefined, undefined, l => fits(me, l)), gg = below.g;
  const x = seatX(me, gg, spotScale(me, { ledge: below.ledge })) ?? clamp(me.x, gg.x1 + 9 * s, Math.max(gg.x1 + 9 * s, gg.x2 - 9 * s));
  const spot = { ledge: below.ledge, dx: x - gg.x1, rest: below.ledge === FLOOR ? 'stand' : chance(0.7) ? 'sit' : 'stand' };
  me.moving = true; me.dest = spot; me.anchor = null;
  const h = gg.y - me.y;
  // A long fall: a flier (or one that can't walk) goes its own way down; the others open a
  // parachute or place a slime block, if they use them. One with its own way down (a chicken
  // fluttering) takes it for any fall that isn't tiny.
  const own = ownTravel(me.design).find(w => can(me, w) && ability(w)?.travel);
  const down = ownTravel(me.design).find(w => can(me, w) && ability(w)?.down);
  const style = walkStyle(me.design), downs = [['parachute', 0.55], ['slime', 0.45]].filter(([w]) => can(me, w));
  if (h > 4 * s && down) await ability(down).travel(spot, g);
  else if (h > 30 * s && own && (style === 'fly' || style === 'none')) await ability(own).travel(spot, g);
  else if (h > 30 * s && downs.length) await WAYS[weighted(downs, d => d[1])[0]](spot, g);
  else if (h > 1) {
    const p = me.pose;
    Object.assign(p, { legs: 'spread', armL: 'up', armR: 'up', eyes: 'wide' });
    const x0 = me.x, y0 = me.y, G = 300 * s;
    const T = Math.sqrt(2 * h / G) * 1000;
    await animate(T, g, t => { const end = spotPoint(me, spot) || { x, y: gg.y }; me.x = lerp(x0, end.x, t); me.y = y0 + (end.y - y0) * t * t; });
    pulse(me, 'squash');
    fx.burst(me.x, me.y, s, ['#E9E2D8', '#D8CFC2'], 6, 5, 0, 320);
    await wait(160, g);
  }
  arrive(me, spot);
}

// ---------- flying ----------
// Up to a cruising height above both ends, then down onto the target.
function flightPath(x0, y0, end, t, s) {
  const peak = Math.min(y0, end.y) - 10 * s;
  const x = lerp(x0, end.x, easeInOut(t));
  const y = t < 0.6 ? lerp(y0, peak, easeOut(t / 0.6)) : lerp(peak, end.y, easeInOut((t - 0.6) / 0.4));
  return { x, y };
}

export async function elytraTo(spot, g) {
  const me = g.me;
  const s = me.s, p = me.pose;
  let end = spotPoint(me, spot);
  if (!end) return;
  const dir = Math.sign(end.x - me.x) || (me.x > env.W / 2 ? -1 : 1);
  // Put the elytra on.
  p.armR = 'up'; await wait(200, g);
  p.pack = 'elytraFolded'; fx.star(me.x + 5 * s, me.y - 6 * s, 420); p.armR = 'side';
  await wait(340, g);
  // Crouch, jump, turn to the side and spread the wings.
  pulse(me, 'squash'); await wait(120, g);
  setShape(me, 'side', 'side', dir);
  const ya = me.y;
  await animate(300, g, t => { me.y = ya - 10 * s * easeOut(t); });
  Object.assign(p, { pack: 'elytraOpen', armR: 'hold', hold: 'rocket' });   // upright and turned, wings out
  const x0 = me.x, y0 = me.y;
  end = spotPoint(me, spot) || end;
  const dur = clamp(Math.hypot(end.x - x0, end.y - y0) / (38 * s), 1.8, 4.2) * 1000;   // unhurried
  const t0 = clockNow();
  for (;;) {
    await tick(g);
    const now = clockNow(), t = Math.min(1, (now - t0) / dur);
    end = spotPoint(me, spot) || end;
    const pt = flightPath(x0, y0, end, t, s);
    me.x = pt.x; me.y = pt.y;
    if (t < 0.55) fx.sparks(me.x - dir * 5 * s, me.y - 2 * s, s, -dir, 0.3);   // the rocket's boost
    else if (p.hold) { p.hold = null; p.armR = 'side'; }
    me.bob = Math.round(Math.sin(now / 140) * 0.6);
    if (t >= 1) break;
  }
  me.bob = 0;
  Object.assign(p, { pack: 'elytraFolded', hold: null, armR: 'side' });
  pulse(me, 'sideSquash');
  setShape(me, 'normal', 'front');
  fx.burst(me.x, me.y, s, ['#E9E2D8', '#D8CFC2'], 6, 5, 0, 320);
  await wait(450, g);
  p.pack = null; fx.puff(me.x, me.y - 4 * s, s);
  await wait(120, g);
}

export async function jetpackTo(spot, g) {
  const me = g.me;
  const s = me.s, p = me.pose;
  let end = spotPoint(me, spot);
  if (!end) return;
  const dir = Math.sign(end.x - me.x) || 1;
  setShape(me, 'side', 'side', dir);
  await wait(300, g);
  p.pack = 'jetNew'; fx.puff(me.x - dir * 4 * s, me.y - 3 * s, s);
  await wait(200, g);
  p.flame = 1; await wait(380, g);                   // the engine sputters
  p.flame = 3;
  const x0 = me.x, y0 = me.y;
  end = spotPoint(me, spot) || end;
  const dur = clamp(Math.hypot(end.x - x0, end.y - y0) / (34 * s), 1.6, 3.8) * 1000;
  const t0 = clockNow();
  for (;;) {
    await tick(g);
    const now = clockNow(), t = Math.min(1, (now - t0) / dur);
    end = spotPoint(me, spot) || end;
    const pt = flightPath(x0, y0, end, t, s);
    me.x = pt.x; me.y = pt.y;
    p.flame = t > 0.8 ? 2 : 3;
    me.bob = Math.round(Math.sin(now / 90));
    if (t >= 1) break;
  }
  me.bob = 0;
  p.flame = 1;
  pulse(me, 'sideSquash');
  await wait(260, g);
  p.flame = 0; fx.puff(me.x - dir * 4 * s, me.y - 3 * s, s);
  await wait(200, g);
  p.pack = null;
  setShape(me, 'normal', 'front');
  await wait(200, g);
}

// The first jetpack, unchanged, for comparing.
export async function jetpackOldTo(spot, g) {
  const me = g.me;
  const s = me.s, p = me.pose;
  setShape(me, 'normal', 'front');
  fx.puff(me.x, me.y - 3 * s, s);
  Object.assign(p, { pack: 'jetOld', flame: 1, eyes: 'up' });
  await wait(380, g);
  Object.assign(p, { flying: true, flame: 3 });
  const x0 = me.x, y0 = me.y;
  let end = spotPoint(me, spot) || { x: x0, y: y0 };
  const dur = clamp(Math.hypot(end.x - x0, end.y - y0) / 230, 0.9, 2.6) * 1000;
  const t0 = clockNow();
  let lastX = x0;
  for (;;) {
    await tick(g);
    const now = clockNow(), t = Math.min(1, (now - t0) / dur);
    end = spotPoint(me, spot) || end;
    const peak = Math.min(y0, end.y) - 46;
    me.x = lerp(x0, end.x, easeInOut(t));
    me.y = t < 0.72 ? lerp(y0, peak, easeInOut(t / 0.72)) : lerp(peak, end.y, easeInOut((t - 0.72) / 0.28));
    me.bob = Math.round(Math.sin(now / 90));
    const vx = me.x - lastX; lastX = me.x;
    p.tilt = vx > 0.6 ? 1 : vx < -0.6 ? -1 : 0;
    p.eyes = p.tilt > 0 ? 'right' : p.tilt < 0 ? 'left' : 'up';
    p.flame = t > 0.8 ? 2 : 3;
    if (t >= 1) break;
  }
  me.bob = 0;
  Object.assign(p, { tilt: 0, flame: 1, flying: false });
  await wait(330, g);
  fx.puff(me.x, me.y - 3 * s, s);
  Object.assign(p, { flame: 0, pack: null, eyes: 'center' });
}

// ---------- coming down ----------
export async function parachuteTo(spot, g) {
  const me = g.me;
  const p = me.pose, s = me.s;
  let end = spotPoint(me, spot) || { x: me.x, y: me.y };
  setShape(me, 'normal', 'front');
  Object.assign(p, { legs: 'spread', armL: 'up', armR: 'up', eyes: 'wide' });   // jump off: a moment of free fall
  const x0 = me.x, y0 = me.y, dir = Math.sign(end.x - x0) || 1;
  const t0 = clockNow();
  for (;;) {
    await tick(g);
    const ts = (clockNow() - t0) / 1000;
    end = spotPoint(me, spot) || end;
    me.x = x0 + dir * Math.min(ts * 14 * s, Math.abs(end.x - x0) * 0.25);
    me.y = Math.min(end.y, y0 - 12 * s * ts + 84 * s * ts * ts);
    if (ts > 0.6 || me.y >= end.y) break;
  }
  const opened = clockNow();
  let baseX = me.x, last = opened;
  const hs = Math.max(12 * s, Math.abs(end.x - baseX) / Math.max(0.6, (end.y - me.y) / (19 * s)));
  while (me.y < end.y - 0.5) {
    await tick(g);
    const now = clockNow(), dt = Math.min(0.1, (now - last) / 1000); last = now;
    end = spotPoint(me, spot) || end;
    me.chute = Math.min(1, (now - opened) / 240);
    if (me.chute >= 1 && p.legs === 'spread') Object.assign(p, { legs: 'stand', eyes: 'center' });
    me.y = Math.min(end.y, me.y + (me.chute < 1 ? 32 : 19) * s * dt);
    baseX += clamp(end.x - baseX, -hs * dt, hs * dt);
    const sway = Math.sin(now / 420) * 2.2 * s * me.chute * clamp((end.y - me.y) / 40, 0, 1);
    me.x = baseX + sway;
  }
  me.x = end.x; me.y = end.y;
  pulse(me, 'squash');
  await wait(110, g);
  await animate(260, g, t => { me.chute = 1 - t; });
  me.chute = 0;
  Object.assign(p, { armL: 'side', armR: 'side', eyes: 'center' });
}

// Jump down, place a slime block mid-fall, bounce on it, hop off, and the block breaks.
export async function slimeTo(spot, g) {
  const me = g.me;
  const p = me.pose, s = me.s, b = 6, bs = b * s;
  let end = spotPoint(me, spot);
  if (!end) return;
  setShape(me, 'normal', 'front');
  Object.assign(p, { legs: 'spread', armL: 'up', armR: 'up', eyes: 'wide' });
  const x0 = me.x, y0 = me.y, G = 300 * s, v0 = -40 * s;
  const fall = Math.max(1, end.y - bs - y0);
  const T = (-v0 + Math.sqrt(v0 * v0 + 2 * G * fall)) / G;
  let block = null, impact = 0;
  const t0 = clockNow();
  for (;;) {
    await tick(g);
    const t = (clockNow() - t0) / 1000;
    end = spotPoint(me, spot) || end;
    me.x = lerp(x0, end.x, Math.min(1, t / T));
    me.y = y0 + v0 * t + 0.5 * G * t * t;
    impact = v0 + G * t;
    if (!block && t > T * 0.5) {
      Object.assign(p, { armR: 'down', hold: 'slime' });
      block = { kind: 'slime', owner: me, x: end.x, y: end.y, s, b, scale: 0.2, squish: 1 };
      props.add(block);
    }
    if (block) { block.x = end.x; block.y = end.y; block.scale = Math.min(1, block.scale + 0.2); if (block.scale >= 1 && p.hold) { p.hold = null; p.armR = 'up'; } }
    if (me.y >= end.y - bs) break;
  }
  p.hold = null;
  if (!block) { block = { kind: 'slime', owner: me, x: end.x, y: end.y, s, b, scale: 1, squish: 1 }; props.add(block); }
  block.scale = 1;
  me.y = end.y - bs;
  // Bounce, smaller each time.
  let v = -impact * 0.55;
  for (let k = 0; k < 3 && -v > 24 * s; k++) {
    pulse(me, 'squash'); block.squish = 0.72;
    fx.burst(me.x, me.y, s, ['#8FD87E', '#B4EBA6'], 5, 4, 0, 260);
    await wait(70, g);
    block.squish = 1;
    Object.assign(p, { armL: 'up', armR: 'up', legs: 'stand', eyes: 'up' });
    const tb = clockNow();
    for (;;) {
      await tick(g);
      const t = (clockNow() - tb) / 1000, y = v * t + 0.5 * G * t * t;
      end = spotPoint(me, spot) || end;
      block.x = end.x; block.y = end.y;
      me.x = end.x; me.y = end.y - bs + Math.min(0, y);
      if (y >= 0 && t > 0.05) break;
    }
    v *= 0.5;
  }
  pulse(me, 'squash');
  Object.assign(p, { armL: 'side', armR: 'side', legs: 'stand', eyes: 'center' });
  await wait(260, g);
  // Hop off beside the block, then it breaks.
  const gg = baseOf(spot).geom();
  if (gg) {
    const off = bs / 2 + 9 * s, right = end.x + off < gg.x2 - 9 * s, left = end.x - off > gg.x1 + 9 * s;
    const dirOff = right && left ? (chance(0.5) ? 1 : -1) : right ? 1 : left ? -1 : 0;
    if (dirOff && spot.rest !== 'lean') spot.dx += dirOff * off;
  }
  await hopTo(() => spotPoint(me, spot), g);
  await wait(200, g);
  fx.burst(block.x, block.y - bs / 2, s, ['#8FD87E', '#4E9A43', '#B4EBA6'], 14, 9, 120, 600);
  props.delete(block);
}

// ---------- ender pearl ----------
export async function pearlTo(spot, g) {
  const me = g.me;
  const p = me.pose, s = me.s;
  let end = spotPoint(me, spot);
  if (!end) return;
  setShape(me, 'normal', 'front');
  const dir = Math.sign(end.x - me.x) || 1, arm = dir > 0 ? 'R' : 'L', armKey = 'arm' + arm;
  Object.assign(p, { holdArm: arm, hold: 'pearl', [armKey]: 'hold', eyes: end.y < me.y - 10 * s ? 'up' : dir > 0 ? 'right' : 'left' });
  await wait(420, g);
  p[armKey] = 'up'; await wait(240, g);              // wind up
  p[armKey] = 'reach'; p.hold = null;
  const hand = me.rig?.hand[arm] || [dir * 8, -4];
  const start = { x: me.x + (hand[0] + 0.5) * s, y: me.y + hand[1] * s };
  const pearl = { kind: 'pearl', owner: me, x: start.x, y: start.y, s };
  props.add(pearl);
  const dist = Math.hypot(end.x - start.x, end.y - start.y);
  const dur = clamp(dist / (90 * s), 0.6, 1.6) * 1000;
  const apex = Math.min(start.y, end.y) - clamp(dist * 0.25, 8 * s, 40 * s);
  await animate(dur, g, t => {
    end = spotPoint(me, spot) || end;
    const cy = 2 * apex - (start.y + end.y) / 2;
    pearl.x = lerp(start.x, end.x, t);
    pearl.y = (1 - t) * (1 - t) * start.y + 2 * (1 - t) * t * cy + t * t * (end.y - s);
    if (chance(0.6)) fx.emit({ kind: 'square', x: pearl.x, y: pearl.y, vx: rand(-1, 1) * s, vy: rand(-1, 1) * s, life: 380, size: 0.45, color: pick(ENDER), shrink: true, fade: 0.9 });
  });
  props.delete(pearl);
  // Gone here in a purple puff, there the same moment.
  fx.burst(me.x, me.y - 3 * s, s, ENDER, 16, 7, -10, 650);
  end = spotPoint(me, spot) || end;
  me.x = end.x; me.y = end.y; me.s = spotScale(me, spot);
  fx.burst(end.x, end.y - 3 * s, me.s, ENDER, 16, 7, -10, 650);
  resetPose(me); pulse(me, 'squash'); p.eyes = 'wide';
  await wait(320, g);
  p.eyes = 'center';
  if (chance(0.35) && hooks.endermite && can(me, 'endermite')) await hooks.endermite(g, spot);
}

// ---------- nether portal ----------
// Room for a portal (or a wormhole), `half` wide each way of its middle and `tall` high, on the
// ledge next to where the companion will stand, with open space above it (nothing covering it).
function portalSite(ledge, nearX, s, half, tall, prefer) {
  const geo = ledge.geom();
  if (!geo) return null;
  const wins = windowList();
  for (const dir of [prefer, -prefer]) {
    const cx = nearX + dir * (half + 8 * s);
    if (cx - half < geo.x1 + 2 || cx + half > geo.x2 - 2) continue;
    if (geo.y - tall < 4) continue;
    const probe = { ownerId: ledge.ownerId };
    const clear = [-half + 2, 0, half - 2].every(dx => visibleOn(probe, cx + dx, geo.y, s, wins) && visibleOn(probe, cx + dx, geo.y - (tall * 3) / 5, s, wins));
    if (clear) return { x: cx, y: geo.y, dir };
  }
  return null;
}

// A frame 4 blocks wide and 5 tall.
export async function portalTo(spot, g, fromLedge) {
  const me = g.me;
  const p = me.pose, s = me.s, b = 4, bs = b * s, BLOCKS = portalBlocks();
  const here = fromLedge ? portalSite(fromLedge, me.x, s, 2 * bs, 5 * bs, me.x < env.W / 2 ? 1 : -1) : null;
  const goal = spotPoint(me, spot);
  const there = goal ? portalSite(baseOf(spot), goal.x, s, 2 * bs, 5 * bs, goal.x < env.W / 2 ? 1 : -1) : null;
  if (!here || !there) return elytraTo(spot, g);     // no room for a portal: fly instead
  setShape(me, 'normal', 'front');
  const dir = here.dir, arm = dir > 0 ? 'R' : 'L', armKey = 'arm' + arm;
  const geo = fromLedge.geom();
  const portal = { kind: 'portal', owner: me, ledge: fromLedge, dx: here.x - geo.x1, x: here.x, y: here.y, s, b, blocks: 0, lit: 0, seed: rand(0, 99) };
  props.add(portal);
  p.holdArm = arm; p.eyes = dir > 0 ? 'right' : 'left';
  const blockCenter = (q, [c, r]) => ({ x: q.x - 2 * bs + (c + 0.5) * bs, y: q.y - 5 * bs + (r + 0.5) * bs });
  const OBSIDIAN = ['#2E2142', '#4B347A', '#150F20'];
  // Place the obsidian one block at a time: the bottom row, both sides upward, then the top.
  for (let i = 0; i < BLOCKS.length; i++) {
    const r = BLOCKS[i][1];
    Object.assign(p, { hold: 'obsidian', [armKey]: r <= 1 ? 'reach' : r <= 2 ? 'side' : 'hold' });
    if (r <= 1) await animate(130, g, t => { me.jump = 4 * 3 * s * t * (1 - t); });
    else await wait(95, g);
    me.jump = 0;
    portal.blocks = i + 1;
    const at = blockCenter(portal, BLOCKS[i]);
    fx.burst(at.x, at.y, s, OBSIDIAN, 5, 3, 40, 260);
    p[armKey] = 'side';
    await wait(45, g);
  }
  // Light it.
  Object.assign(p, { hold: 'flint', [armKey]: 'hold' });
  await wait(300, g);
  for (let k = 0; k < 3; k++) fx.sparks(portal.x, portal.y - bs * 1.2, s, -dir * 0.4, -0.6);
  await wait(140, g);
  await animate(480, g, t => { portal.lit = t; });
  Object.assign(p, { hold: null, [armKey]: 'side', eyes: 'center' });
  for (let k = 0; k < 8; k++) fx.emit({ kind: 'square', x: portal.x + rand(-1, 1) * bs, y: portal.y - rand(1, 4) * bs, vy: -rand(4, 9) * s, life: 700, size: 0.5, color: pick(ENDER), fade: 0.9 });
  await wait(300, g);
  // Jump in, as in the game: up onto the bottom block, into the swirl, and gone.
  const inPortal = q => () => ({ x: q.x, y: q.y - bs });
  await hopTo(inPortal(portal), g);
  p.eyes = 'up';
  await animate(560, g, t => {
    const q = inPortal(portal)();
    me.x = q.x; me.y = q.y; me.alpha = 1 - t;
    if (chance(0.5)) fx.emit({ kind: 'square', x: me.x + rand(-4, 4) * s, y: me.y - rand(0, 6) * s, vy: -rand(3, 7) * s, life: 600, size: 0.45, color: pick(ENDER), fade: 0.9 });
  });
  me.alpha = 0;
  // The first portal comes apart while the second forms at the destination.
  // (If its window closed meanwhile, the second one opens where it was and stays there.)
  const tgeo = baseOf(spot).geom();
  const exit = { kind: 'portal', owner: me, ledge: tgeo ? baseOf(spot) : null, dx: tgeo ? there.x - tgeo.x1 : 0, x: there.x, y: there.y, s, b, blocks: 0, lit: 0, seed: rand(0, 99) };
  props.add(exit);
  await closeAndOpen(portal, exit, g);
  // Come out standing on the second portal's bottom block, then jump down beside it.
  p.eyes = 'center';
  await animate(450, g, t => {
    const q = inPortal(exit)();
    me.x = q.x; me.y = q.y; me.alpha = t;
    if (chance(0.5)) fx.emit({ kind: 'square', x: me.x + rand(-4, 4) * s, y: me.y - rand(0, 6) * s, vy: -rand(3, 7) * s, life: 600, size: 0.45, color: pick(ENDER), fade: 0.9 });
  });
  me.alpha = 1;
  await wait(200, g);
  const side = Math.sign((goal?.x ?? exit.x + 1) - exit.x) || 1;
  const landDx = exit.dx + side * (2 * bs + 10 * s);
  await hopTo(() => {
    const gg = baseOf(spot).geom();
    return gg ? { x: clamp(gg.x1 + landDx, gg.x1 + 9 * s, gg.x2 - 9 * s), y: gg.y } : null;
  }, g, spotScale(me, spot));
  const pt = spotPoint(me, spot);
  if (pt) await walkOn(baseOf(spot), pt.x, g);
  await wait(200, g);
  await closePortal(exit, g);
}

async function closeAndOpen(a, b, g) {
  const n = portalBlocks().length;
  await animate(420, g, t => { a.lit = 1 - t; });
  a.shrink = Array(n).fill(1);
  for (let i = 0; i < n; i++) {
    b.blocks = i + 1;
    await animate(55, g, t => { a.shrink[n - 1 - i] = 1 - t; });
  }
  props.delete(a);
  await animate(380, g, t => { b.lit = t; });
}

async function closePortal(portal, g) {
  const n = portalBlocks().length, bs = portal.b * portal.s;
  await animate(420, g, t => { portal.lit = 1 - t; });
  portal.shrink = Array(n).fill(1);
  for (let i = n - 1; i >= 0; i--) {
    const [c, r] = portalBlocks()[i];
    fx.burst(portal.x - 2 * bs + (c + 0.5) * bs, portal.y - 5 * bs + (r + 0.5) * bs, portal.s, ['#2E2142', '#4B347A', '#150F20'], 4, 3, 60, 300);
    await animate(65, g, t => { portal.shrink[i] = 1 - t; });
  }
  props.delete(portal);
}

// ---------- grappling hook (the desktop way for the ender pearl) ----------
// A little black gun shoots a hook on a rope at the spot. It bites, the rope snaps taut and
// reels the companion over: it swings in under the edge like a pendulum on a shortening rope,
// past and back, and climbs up. Going down, the rope lowers it down to the hook instead. The
// gun, rope and hook are one prop (see drawHook in stage.js).
const turnTo = (a, b, t) => a + Math.atan2(Math.sin(b - a), Math.cos(b - a)) * t;   // an angle, the short way round
const DUST = ['#EDE7DF', '#D8CFC2'];

export async function hookTo(spot, g) {
  const me = g.me, p = me.pose, s = me.s;
  let end = spotPoint(me, spot);
  if (!end) return;
  if (Math.hypot(end.x - me.x, end.y - me.y) < 16 * s) return WAYS.hop(spot, g);   // too close for it
  setShape(me, 'normal', 'front');
  const target = () => (end = spotPoint(me, spot) || end);   // where the hook bites, following its window
  const dir = Math.sign(end.x - me.x) || 1, arm = dir > 0 ? 'R' : 'L', armKey = 'arm' + arm, otherKey = dir > 0 ? 'armL' : 'armR';
  const down = end.y > me.y + 8 * s;
  const gun = { kind: 'hook', owner: me, of: me, arm, aim: dir > 0 ? 0 : Math.PI, target, hook: null, sag: 0, loaded: true };
  // Out comes the gun, and it takes aim.
  Object.assign(p, { [armKey]: 'hold', eyes: down ? 'down' : end.y < me.y - 10 * s ? 'up' : dir > 0 ? 'right' : 'left' });
  props.add(gun);
  const h0 = handPoint(me, arm);
  fx.burst(h0.x, h0.y, s, DUST, 5, 4, 0, 260);
  await wait(260, g);
  const aimAt = () => { const h = handPoint(me, arm), t = target(); return Math.atan2(t.y - h.y, t.x - h.x); };
  if (aimAt() < -0.6 && aimAt() > 0.6 - Math.PI) p[armKey] = 'reach';   // raised, for a spot up high
  const a0 = gun.aim;
  await animate(220, g, t => { gun.aim = turnTo(a0, aimAt(), easeOut(t)); });
  gun.aim = null;                                           // from now on it points at the hook
  await wait(160, g);
  // Fire: the gun kicks, and the hook flies with the rope paying out loose behind it.
  const from = gunMuzzle(gun);
  gun.loaded = false; gun.kick = clockNow();
  fx.burst(from.x, from.y, s, ['#FFFFFF', '#E9E2D8'], 5, 5, 0, 220);
  const dist = Math.hypot(end.x - from.x, end.y - from.y), lift = Math.min(dist * 0.12, 10 * s);
  await animate(clamp(dist / (170 * s), 0.22, 0.6) * 1000, g, t => {
    const to = target(), x = lerp(from.x, to.x, t), y = lerp(from.y, to.y, t) - Math.sin(Math.PI * t) * lift;
    gun.hook = { x, y, angle: Math.atan2(to.y - from.y - Math.cos(Math.PI * t) * Math.PI * lift, to.x - from.x) };
    gun.sag = 0.12 * Math.hypot(x - from.x, y - from.y);
  });
  // It bites with a clink, and the rope snaps taut.
  const bite = target(), sag0 = gun.sag, onHook = () => { const at = target(); gun.hook = { ...gun.hook, x: at.x, y: at.y }; };
  fx.burst(bite.x, bite.y, s, ['#FFFFFF', '#FFE38A', '#D7DCE3'], 7, 7, 0, 280);
  fx.star(bite.x, bite.y - 2 * s, 380);
  if (!down) {                                             // it grabs on with both hands, ready to be yanked up
    Object.assign(p, { armL: 'up', armR: 'up', legs: 'spread', eyes: 'up' });
    setShape(me, 'stretch');
    me.rotAround = 'top';
  }
  await animate(150, g, t => { gun.sag = sag0 * (1 - t) * (1 - t); onHook(); });
  gun.sag = 0;
  if (down) {
    // Lowered down to it: a hop off, out and down along the rope, and a soft landing on the hook.
    Object.assign(p, { [otherKey]: 'up', legs: 'spread' });
    pulse(me, 'squash'); await wait(90, g);
    setShape(me, 'stretch');
    me.rotAround = 'center';
    const x0 = me.x, y0 = me.y;
    await animate(clamp(Math.hypot(end.x - x0, end.y - y0) / (60 * s), 0.7, 1.4) * 1000, g, t => {
      const at = target();
      me.x = lerp(x0, at.x, easeOut(t));
      me.y = lerp(y0, at.y, t * t) - Math.sin(Math.PI * Math.min(1, t * 1.6)) * 6 * s * (1 - t);
      me.rot = -dir * 0.3 * Math.sin(Math.PI * t);         // leaning back against the rope
      onHook();
    });
    me.rot = 0; me.rotAround = 'feet';
    setShape(me, 'normal');
    pulse(me, 'squash');
    fx.burst(me.x, me.y, s, DUST, 6, 5, 0, 320);
    Object.assign(p, { [otherKey]: 'side', legs: 'stand', eyes: 'center' });
    await reelIn(gun, g);
  } else {
    // Reeled in: yanked up along the rope, it swings in under the hook, past it and back, and
    // hangs there from the gun, its body tilted along the rope.
    const h = handPoint(me, arm), x0 = h.x - end.x, y0 = h.y - end.y;
    const th0 = Math.atan2(x0, y0), r0 = Math.hypot(x0, y0), r1 = 9 * s;   // r1: the gun just under the hook
    const swing = u => Math.exp(-3.2 * u) * (Math.cos(6.35 * u) + 0.5 * Math.sin(6.35 * u));   // 1 to 0: in, a fifth past, back
    await animate(clamp(r0 / (55 * s), 0.9, 1.6) * 1000 + 300, g, u => {
      const at = target(), th = th0 * swing(u);            // the rope's angle, from straight down
      const r = r1 + (r0 - r1) * (1 - easeOut(Math.min(1, u / 0.75)));   // the rope, reeling in
      me.rot = clamp(-th * 0.85, -0.6, 0.6) * Math.min(1, u / 0.12);
      const hand = handPoint(me, arm);
      me.x += at.x + r * Math.sin(th) - hand.x;
      me.y = Math.min(me.y + at.y + r * Math.cos(th) - hand.y, floorY());   // never through the floor
      onHook();
    });
    // It lets go (the hook zips back into the gun) and pulls itself up there.
    const rot0 = me.rot;
    await reelIn(gun, g, t => { me.rot = rot0 * (1 - t); });
    me.rot = 0; me.rotAround = 'feet';
    gun.aim = dir > 0 ? -0.5 : Math.PI + 0.5;
    await hopTo(() => spotPoint(me, spot), g, spotScale(me, spot));
  }
  // The gun goes away.
  await wait(200, g);
  const h1 = handPoint(me, arm);
  fx.burst(h1.x, h1.y, s, DUST, 5, 4, 0, 260);
  props.delete(gun);
  Object.assign(p, { armL: 'side', armR: 'side', eyes: 'center' });
}

// The hook lets go and zips back into the gun along its rope; also(t) runs alongside.
async function reelIn(gun, g, also = () => {}) {
  const hook = gun.hook;
  await animate(140, g, t => {
    const m = gunMuzzle(gun);
    gun.hook = { ...hook, x: lerp(hook.x, m.x, t), y: lerp(hook.y, m.y, t) };
    also(t);
  });
  gun.hook = null; gun.loaded = true;
}

// ---------- wormhole (the desktop way for the nether portal) ----------
// It throws a glowing disc that springs open into a swirling wormhole beside it, and a matching
// one opens at the destination. It steps in and spins away into the swirl, spins out of the
// other one while the first closes, steps out, and that one closes too.
const easeOutBack = t => 1 + 2.7 * (t - 1) ** 3 + 1.7 * (t - 1) ** 2;   // overshoots a little, then settles

// A wormhole a little taller than the companion and wide enough for it (see wormholePixels).
function wormholeSize(me) {
  const sh = me.shapes.normal, tall = sh.bh + sh.leg;
  const w = clamp(Math.round(Math.max(tall * 0.6, sh.bw * 0.75)), 7, 17);
  return { w, h: clamp(Math.round(Math.max(tall + 4, w * 1.4)), 12, 26) };
}

// It springs open out of its disc in a burst of sparkles; shut() folds it back into the disc,
// which vanishes in a twinkle.
async function openWormhole(hole, g) {
  fx.burst(hole.x, hole.y - 2 * hole.s, hole.s, WORMHOLE_COLORS, 10, 8, 0, 420);
  await animate(520, g, t => { hole.open = easeOutBack(t); });
}
function shut(hole) {
  const x = hole.x, y = hole.y - 2 * hole.s;
  fx.star(x, y, 420);
  fx.burst(x, y, hole.s, WORMHOLE_COLORS, 12, 9, 0, 420);
  props.delete(hole);
}

export async function wormholeTo(spot, g, fromLedge) {
  const me = g.me, p = me.pose, s = me.s, { w, h } = wormholeSize(me);
  const half = Math.ceil(w / 2) * s, tall = (h + 1) * s;
  const here = fromLedge ? portalSite(fromLedge, me.x, s, half, tall, me.x < env.W / 2 ? 1 : -1) : null;
  const goal = spotPoint(me, spot);
  const there = goal ? portalSite(baseOf(spot), goal.x, s, half, tall, goal.x < env.W / 2 ? 1 : -1) : null;
  if (!here || !there) return jetpackTo(spot, g);     // no room for one: fly instead
  setShape(me, 'normal', 'front');
  const dir = here.dir, arm = dir > 0 ? 'R' : 'L', armKey = 'arm' + arm;
  const hole = (site, ledge) => ({ kind: 'wormhole', owner: me, ledge, dx: site.x - ledge.geom().x1, x: site.x, y: site.y, s, w, h, open: 0, disc: null, seed: rand(0, 99) });
  const entry = hole(here, fromLedge), exit = hole(there, baseOf(spot));
  // Out comes the disc, and it's thrown to where the wormhole goes.
  Object.assign(p, { holdArm: arm, hold: 'disc', [armKey]: 'hold', eyes: dir > 0 ? 'right' : 'left' });
  await wait(380, g);
  p[armKey] = 'up'; await wait(200, g);              // wind up
  p[armKey] = 'reach'; p.hold = null;
  const from = handPoint(me, arm);
  props.add(entry);
  await animate(380, g, t => { entry.disc = { x: lerp(from.x, entry.x, t), y: lerp(from.y, entry.y - 2 * s, t) - Math.sin(Math.PI * t) * 8 * s }; });
  entry.disc = null;
  p[armKey] = 'side';
  // It springs open, and a matching one opens over there.
  await openWormhole(entry, g);
  props.add(exit);
  await openWormhole(exit, g);
  await wait(250, g);
  // In it goes, pulled up into the middle of the swirl, spinning away.
  await walkOn(fromLedge, entry.x, g);
  const lift = Math.max(0, ((h - me.shape.bh - me.shape.leg) / 2) * s);   // up to the swirl's middle
  p.eyes = 'wide';
  me.rotAround = 'center';
  const y0 = me.y;
  await animate(620, g, t => { me.y = y0 - lift * easeOut(t); me.rot = dir * 3 * Math.PI * t * t; me.alpha = 1 - t * t; });
  me.alpha = 0;
  // Out of the other one it spins, while the first one closes behind it.
  me.s = spotScale(me, spot);
  await animate(620, g, t => {
    const k = 1 - t;
    me.x = exit.x; me.y = exit.y - lift * k * k;
    me.rot = -dir * 3 * Math.PI * k * k; me.alpha = 1 - k * k;
    entry.open = 1 - t * t;
  });
  shut(entry);
  me.rot = 0; me.rotAround = 'feet'; me.alpha = 1;
  pulse(me, 'squash'); p.eyes = 'center';
  await wait(220, g);
  // It steps out to the spot, and that one closes too.
  const pt = spotPoint(me, spot);
  if (pt) await walkOn(baseOf(spot), pt.x, g);
  await animate(320, g, t => { exit.open = 1 - t * t; });
  shut(exit);
}

export const WAYS = {
  elytra: elytraTo, jetpack: jetpackTo, jetpackOld: jetpackOldTo, parachute: parachuteTo, slime: slimeTo,
  pearl: pearlTo, portal: portalTo, hook: hookTo, wormhole: wormholeTo,
  hop: (spot, g) => hopTo(() => spotPoint(g.me, spot), g, spotScale(g.me, spot)),
};
